import '@/styles/animations.css';

/**
 * Value proposition data structure
 */
interface ValueProposition {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: 'network' | 'global' | 'learn' | 'power' | 'time' | 'exclusive';
}

/**
 * Props for the ValuePropositionCards component
 */
interface ValuePropositionCardsProps {
  /** Optional CSS class name for the container */
  className?: string;
}

/**
 * Default value propositions for FOMO benefit display
 */
const defaultValuePropositions: ValueProposition[] = [
  {
    id: '1',
    title: 'Mega Networking Event',
    subtitle: 'Scale & Importance',
    description: 'Join the biggest reunion of the 1996 batch. Over 500+ alumni expected from across the globe. This is THE event of 2026 you cannot miss.',
    icon: 'network',
  },
  {
    id: '2',
    title: 'Grassroots to Global',
    subtitle: 'Worldwide Connections',
    description: 'Connect with classmates who have spread across the world. From local entrepreneurs to global executives, your network awaits.',
    icon: 'global',
  },
  {
    id: '3',
    title: 'Learn Life Lessons',
    subtitle: 'Peer Wisdom',
    description: 'Discover insights from peers who have navigated diverse career paths. Real stories, real lessons, from people who walked the same school corridors.',
    icon: 'learn',
  },
  {
    id: '4',
    title: 'Power Up Your Network',
    subtitle: 'Career Benefits',
    description: 'Reconnect with professionals across industries. Doctors, engineers, artists, entrepreneurs - your batch has it all. Unlock opportunities through shared history.',
    icon: 'power',
  },
];

/**
 * FOMO trigger messages for urgency
 */
const fomoTriggers = [
  { text: 'Once in 30 years', highlight: true },
  { text: "Don't let another decade pass", highlight: false },
  { text: 'Limited seats available', highlight: true },
];

/**
 * Get the icon SVG for a value proposition
 */
function getPropositionIcon(iconType: ValueProposition['icon']): React.ReactNode {
  switch (iconType) {
    case 'network':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case 'global':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'learn':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    case 'power':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'time':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'exclusive':
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    default:
      return (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
  }
}

/**
 * Individual value proposition card component
 */
interface ValueCardProps {
  proposition: ValueProposition;
  index: number;
}

function ValueCard({ proposition, index }: ValueCardProps) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] opacity-0 animate-fade-in-up"
      style={{
        animationDelay: `${index * 150}ms`,
        animationFillMode: 'forwards',
        background: 'linear-gradient(135deg, rgba(45, 45, 74, 0.8) 0%, rgba(26, 26, 46, 0.8) 100%)',
        border: '1px solid rgba(255, 143, 163, 0.2)',
      }}
    >
      {/* Hover glow effect */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(255, 143, 163, 0.2) 0%, transparent 70%)',
        }}
      />

      {/* Sparkle decoration */}
      <div
        className="absolute top-4 right-4 w-2 h-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-twinkle"
        style={{ background: 'rgba(255, 215, 0, 0.8)' }}
        aria-hidden="true"
      />

      <div className="relative p-6 md:p-8">
        {/* Icon container */}
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-romantic-rose mb-5 group-hover:scale-110 transition-transform duration-300"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 143, 163, 0.2) 0%, rgba(255, 143, 163, 0.05) 100%)',
            border: '1px solid rgba(255, 143, 163, 0.3)',
          }}
        >
          {getPropositionIcon(proposition.icon)}
        </div>

        {/* Subtitle badge */}
        <div className="mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-romantic-gold">
            {proposition.subtitle}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-3 group-hover:text-romantic-rose transition-colors">
          {proposition.title}
        </h3>

        {/* Description */}
        <p className="text-white/70 text-sm md:text-base leading-relaxed">
          {proposition.description}
        </p>

        {/* Decorative corner gradient */}
        <div
          className="absolute bottom-0 right-0 w-32 h-32 pointer-events-none opacity-30 group-hover:opacity-60 transition-opacity"
          style={{
            background: 'radial-gradient(circle at 100% 100%, rgba(255, 143, 163, 0.15) 0%, transparent 70%)',
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

/**
 * FOMO urgency banner component
 */
function FomoUrgencyBanner() {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-4 md:gap-8 py-4 px-6 rounded-2xl opacity-0 animate-fade-in-up"
      style={{
        animationDelay: '600ms',
        animationFillMode: 'forwards',
        background: 'linear-gradient(135deg, rgba(255, 143, 163, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
        border: '1px solid rgba(255, 215, 0, 0.2)',
      }}
    >
      {fomoTriggers.map((trigger, index) => (
        <div
          key={index}
          className="flex items-center gap-2"
        >
          {trigger.highlight && (
            <span className="w-2 h-2 rounded-full bg-romantic-gold animate-pulse" aria-hidden="true" />
          )}
          <span className={`text-sm md:text-base font-medium ${trigger.highlight ? 'text-romantic-gold' : 'text-white/70'}`}>
            {trigger.text}
          </span>
          {index < fomoTriggers.length - 1 && (
            <span className="text-white/30 hidden md:inline ml-4">|</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * ValuePropositionCards Component
 *
 * Displays a grid of FOMO-inducing value proposition cards that highlight
 * the benefits of attending the reunion. Emphasizes networking, global
 * connections, peer learning, and career opportunities.
 *
 * Features:
 * - Responsive grid layout (1-2 columns)
 * - Staggered entrance animations
 * - Hover effects with glow
 * - Icon-based visual indicators
 * - FOMO urgency banner
 * - Romantic color palette accents
 *
 * @example
 * ```tsx
 * <ValuePropositionCards />
 *
 * <ValuePropositionCards className="my-8" />
 * ```
 */
export function ValuePropositionCards({ className = '' }: ValuePropositionCardsProps) {
  return (
    <section className={`py-16 md:py-20 px-4 relative overflow-hidden ${className}`}>
      {/* Background gradient */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 143, 163, 0.05) 0%, transparent 40%, rgba(255, 215, 0, 0.05) 100%)',
        }}
        aria-hidden="true"
      />

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 opacity-0 animate-fade-in-up" style={{ animationFillMode: 'forwards' }}>
          <div className="inline-block px-4 py-2 rounded-full bg-romantic-rose/20 border border-romantic-rose/30 mb-4">
            <span className="text-sm text-romantic-rose font-medium uppercase tracking-wider">
              Why Join Us
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4">
            The <span className="text-gradient">Mega Event</span> of 2026
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base md:text-lg">
            This isn't just a reunion - it's the networking opportunity of a lifetime.
            30 years of success stories coming together under one roof.
          </p>
        </div>

        {/* Value proposition cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {defaultValuePropositions.map((proposition, index) => (
            <ValueCard key={proposition.id} proposition={proposition} index={index} />
          ))}
        </div>

        {/* FOMO urgency banner */}
        <FomoUrgencyBanner />

        {/* Call to action */}
        <div
          className="mt-10 text-center opacity-0 animate-fade-in-up"
          style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}
        >
          <p className="text-white/50 text-sm mb-4">
            Seats are filling up fast. Don't miss this once-in-a-generation gathering.
          </p>
        </div>
      </div>
    </section>
  );
}

export default ValuePropositionCards;
